import { Injectable } from '@angular/core';

import { firstValueFrom } from 'rxjs';

import { ApiService } from '@services/api/api';
import { LanguageService } from '@services/language/language.service';

import { ApiResponse } from '@models/interfaces';
import { LanguagesEnum, TLanguage } from '@core/config';

import { HttpHelper, SpsLogger } from '@lib/utils';
import { NodeRouter } from '@generated/router';

import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class CmsRestApiService {
  static extractLanguage(path: string): TLanguage | null {
    const languages = Object.keys(LanguagesEnum);
    const regex = RegExp(`^\/?(?<lang>(${languages.join('|')}))\/?`);

    let selectedLanguage: TLanguage = null;

    try {
      selectedLanguage = regex.exec(path?.toUpperCase())?.groups?.lang as TLanguage;

      if (!selectedLanguage) {
        SpsLogger.log(`Unable to extract language from path: "${path}"`);
      }
    } catch (e) {
      SpsLogger.log(`Error extracting language from path: "${path}"`, e);
    }

    return selectedLanguage ?? null;
  }

  constructor(private api: ApiService, private languageService: LanguageService) {}

  public async getTranslatePath(target: string): Promise<NodeRouter> {
    let { path, language } = this.getPathAndLang(target);

    // Remove legacy params
    if (path.includes(';')) {
      path = path.slice(0, path.indexOf(';'));
    }

    if (!LanguageService.isSelectedLanguage(language)) {
      this.languageService.useLanguage(language);
    }

    let localizedPath = HttpHelper.getLocalizedPath('router/translate-path');
    if (localizedPath.startsWith('/')) {
      localizedPath = localizedPath.slice(1);
    }

    const url = [environment.apiHost, localizedPath].join('/');

    // Hotfix start
    if (path === '/home') {
      path = `/${language}/home`;
    }
    // Hotfix end

    // TODO: TEST RUN ONLY!
    // Remove doubled slashes, prefix with language to bypass cms redirect collisions.
    // path = path.startsWith(`/${language}`) ? path : `/${language}/${path}`;
    // path = path.replaceAll('//', '/');
    if(path.indexOf("origin")!=-1){
      path = "/en/home";
    }

    const response = await firstValueFrom(this.api.get<NodeRouter>(null, { path }, url));

    if (response.error) {
      return Promise.reject(`Error getting translate path for path ${target}: ${response.error?.message}`);
    }

    return (response as ApiResponse<NodeRouter>).payload;
  }

  private getPathAndLang(target: string): { path: string; language?: string } {
    const languages = Object.keys(LanguagesEnum).map(key => key.toLowerCase());
    const regex = new RegExp(`^/(?<lang>(${languages.join('|')}))(?<rawPath>(\/(.+)))`);

    let path = target;
    let language: string = null;

    if (regex.test(target.toLowerCase())) {
      const { lang, rawPath } = regex.exec(target)?.groups;

      path = rawPath;
      language = lang ?? null;
    }

    return { path, language };
  }
}
